import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import {
  ContractResponseDto,
  ContractResponseDtoContractStatusEnum,
  ContractResponseDtoTransactionTypeEnum,
  SubmissionResponseDto,
  UpdateContractPayloadDtoPolicyTypeEnum,
} from 'libs/pinnacle-sdk';
import { isNil } from 'lodash';
import { getLocalDateForMidnightUTC } from 'utils';
import * as yup from 'yup';
import { isCancelledOrNTUContract } from '../validation-helper';

export interface TransactionDetailsFormValues {
  pinnacleReferenceNumber?: string;
  contractStatus: ContractResponseDtoContractStatusEnum;
  bindingDate: Dayjs;
  inceptionDate: Dayjs;
  expiryDate: Dayjs;
  premiumPaymentWarranty: number;
  paymentWarrantyDate?: Dayjs;
  policyType: UpdateContractPayloadDtoPolicyTypeEnum;
  transactionType?: ContractResponseDtoTransactionTypeEnum;
  lineOfBusiness: string;
  subClass: string;
  binder?: string;
  namedUnderwriterId?: string;
  insured?: string;
  reinsured?: string;
  broker?: string;
  brokerReference?: string;
  underwritingYear?: number;
  installments: boolean;
  principalOccupation?: string;
  mainRiskTerritory?: string;
  isDeclaration?: boolean | null;
  oldPinReferenceNumber?: string;
}

export const getTransactionDetailsValidationSchemaShape = (
  t: TFunction,
  tbaConfig?: { tbaReinsuredIds?: string[]; tbaBrokerIds?: string[] }
) => ({
  contractStatus: yup
    .string()
    .required(
      t('validation.required', { field: t('contractDetail.contractStatus') })
    ),
  bindingDate: yup
    .date()
    .required(
      t('validation.required', { field: t('contractDetail.bindingDate') })
    ),
  premiumPaymentWarranty: yup.string().required(
    t('validation.required', {
      field: t('contractDetail.premiumPaymentWarranty'),
    })
  ),
  inceptionDate: yup
    .date()
    .required(
      t('validation.required', { field: t('contractDetail.inceptionDate') })
    ),
  expiryDate: yup
    .date()
    .required(
      t('validation.required', { field: t('contractDetail.expiryDate') })
    ),
  policyType: yup
    .string()
    .required(
      t('validation.required', { field: t('contractDetail.policyType') })
    ),
  transactionType: yup
    .string()
    .required(
      t('validation.required', { field: t('contractDetail.transactionType') })
    ),
  lineOfBusiness: yup
    .string()
    .required(
      t('validation.required', { field: t('contractDetail.lineOfBusiness') })
    ),
  subClass: yup
    .string()
    .required(
      t('validation.required', { field: t('contractDetail.subClass') })
    ),
  binder: yup.string(),
  namedUnderwriterId: yup
    .string()
    .required(
      t('validation.required', { field: t('submission.namedUnderwriter') })
    ),
  insured: yup.string(),
  reinsured: yup
    .mixed()
    .test(
      'reinsured-not-tba',
      t('validation.invalidOption', { field: t('contractDetail.reinsured') }),
      (value, context) => {
        const { contractStatus } = context.parent;

        if (isCancelledOrNTUContract(contractStatus)) {
          return true;
        }

        if (isNil(value)) return true;
        return !tbaConfig?.tbaReinsuredIds?.includes(value as string);
      }
    ),
  broker: yup
    .mixed()
    .test(
      'broker-not-tba',
      t('validation.invalidOption', { field: t('contractDetail.broker') }),
      (value, context) => {
        const { contractStatus } = context.parent;

        if (isCancelledOrNTUContract(contractStatus)) {
          return true;
        }

        if (isNil(value)) return true;
        return !tbaConfig?.tbaBrokerIds?.includes(value as string);
      }
    ),
  brokerReference: yup.string(),
  underwritingYear: yup.number(),
  installments: yup
    .boolean()
    .required(
      t('validation.required', { field: t('contractDetail.installments') })
    ),
  principalOccupation: yup.string(),
  mainRiskTerritory: yup.string(),
  isDeclaration: yup.boolean().nullable(),
  oldPinnacleReferenceNumber: yup.string(),
});

export const getTransactionDetailInitialValues = (
  submission: SubmissionResponseDto,
  contract?: ContractResponseDto
): TransactionDetailsFormValues => {
  return {
    pinnacleReferenceNumber: contract?.pinnacleReferenceNumber,
    contractStatus:
      isNil(contract?.contractStatus) ||
      contract?.contractStatus === ContractResponseDtoContractStatusEnum.Quoted
        ? ContractResponseDtoContractStatusEnum.Bound
        : contract?.contractStatus,
    bindingDate: contract?.bindingDate ? dayjs(contract?.bindingDate) : dayjs(),
    premiumPaymentWarranty: contract?.premiumPaymentWarranty
      ? Number(contract?.premiumPaymentWarranty)
      : 120,
    brokerReference: contract?.brokerReference,
    installments: contract?.installments ?? false,
    inceptionDate: getLocalDateForMidnightUTC(submission.inceptionDate),
    expiryDate: getLocalDateForMidnightUTC(submission.expiryDate ?? new Date()),
    policyType: contract?.policyType || submission.policyType,
    transactionType: contract?.transactionType || submission?.transactionType,
    lineOfBusiness: submission.lineOfBusiness,
    subClass: submission.subClass,
    binder: contract?.binder?.binderName,
    namedUnderwriterId: contract?.underwriter ?? submission.namedUnderwriterId,
    insured: submission.insured?.name,
    reinsured: contract?.reinsured?.reinsuredId,
    broker: contract?.broker?.brokerId,
    underwritingYear: contract?.binder?.underwritingYear,
    principalOccupation: submission.principalOccupation,
    mainRiskTerritory: submission.mainRegionAndRiskTerritory,
    isDeclaration: contract?.isDeclaration ?? false,
    oldPinReferenceNumber: contract?.oldPinReferenceNumber,
  };
};

export const calculatePaymentWarrantyDate = (
  inceptionDate: Dayjs,
  premiumPaymentWarranty: number
): Dayjs => {
  return inceptionDate.add(premiumPaymentWarranty, 'day');
};
